import { Head, Link, usePage } from "@inertiajs/react"
import { ImageOff, Pencil, Plus } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { AdminPageHeader, EmptyState } from "@/components/admin/AdminPageHeader"
import { ConfirmDestroy } from "@/components/admin/ConfirmDestroy"
import { plural } from "@/components/admin/format"
import { MoveButtons } from "@/components/admin/MoveButtons"
import { ResponsiveList } from "@/components/admin/ResponsiveList"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { AdminGalleryIndexProps, AdminGalleryRow, PageProps } from "@/types/inertia"

const TITLE = "Gallery"
const DESCRIPTION =
  "The bottles on the public gallery and occasion pages: add a piece, edit its title, caption and occasion, reorder or remove it."

export default function AdminGalleryIndex() {
  const { props } = usePage<PageProps<AdminGalleryIndexProps>>()
  const { items } = props

  return (
    <>
      <Head title={TITLE}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <AdminPageHeader
          title="Gallery"
          description="Pieces show on the Gallery page and on their occasion's page, in this order."
          actions={
            <Button asChild size="lg">
              <Link href="/admin/gallery_items/new">
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add a piece
              </Link>
            </Button>
          }
        />

        <p className="mt-6">{plural(items.length, "piece")}, in display order.</p>

        <div className="mt-3">
          {items.length === 0 ? (
            <EmptyState>
              The gallery is empty, so the occasion pages show their sample bottles. Add a piece to start it.
            </EmptyState>
          ) : (
            <ResponsiveList
              label="Gallery pieces"
              items={items}
              getKey={(item) => item.id}
              card={(item) => <GalleryCard item={item} />}
              columns={[
                {
                  key: "order",
                  header: "Order",
                  className: "w-28",
                  cell: (item) => (
                    <MoveButtons url={item.move_url} itemName={item.title} first={item.first} last={item.last} />
                  ),
                },
                {
                  key: "image",
                  header: "Image",
                  srOnlyHeader: true,
                  className: "w-20",
                  cell: (item) => <GalleryThumb item={item} />,
                },
                {
                  key: "piece",
                  header: "Piece",
                  cell: (item) => (
                    <div className="min-w-0">
                      <div className="font-medium">
                        <Link href={item.edit_url}>{item.title}</Link>
                      </div>
                      <div className="text-xs text-ink-muted">{item.caption ?? "No caption"}</div>
                    </div>
                  ),
                },
                {
                  key: "occasion",
                  header: "Occasion",
                  className: "whitespace-nowrap",
                  cell: (item) => <Badge tone="neutral">{item.occasion_label}</Badge>,
                },
                {
                  key: "actions",
                  header: "Actions",
                  srOnlyHeader: true,
                  // No fixed width: at 768px a 240px column pushed Remove out of view.
                  className: "whitespace-nowrap text-right",
                  cell: (item) => <GalleryActions item={item} />,
                },
              ]}
            />
          )}
        </div>
      </AdminShell>
    </>
  )
}

function GalleryThumb({ item }: { item: AdminGalleryRow }) {
  return (
    <div className="flex h-20 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md bg-surface">
      {item.image_url ? (
        <img
          src={item.image_url}
          alt=""
          loading="lazy"
          decoding="async"
          width={64}
          height={80}
          className="h-full w-full object-contain"
        />
      ) : (
        <ImageOff className="h-5 w-5 text-ink-muted" aria-label="No image" />
      )}
    </div>
  )
}

function GalleryActions({ item }: { item: AdminGalleryRow }) {
  return (
    // Side by side from 1280px; stacked where the table is narrower (tablet, and beside the rail).
    <div className="flex flex-wrap items-center justify-end gap-1 xl:flex-nowrap">
      <Button asChild variant="secondary" size="lg">
        <Link href={item.edit_url}>
          <Pencil className="h-4 w-4" aria-hidden="true" />
          Edit<span className="sr-only"> {item.title}</span>
        </Link>
      </Button>
      <ConfirmDestroy
        url={item.destroy_url}
        itemName={item.title}
        title={`Remove ${item.title}?`}
        description="It leaves the Gallery page and its occasion page straight away, and its image is deleted. This cannot be undone."
      />
    </div>
  )
}

function GalleryCard({ item }: { item: AdminGalleryRow }) {
  return (
    <div className="px-4 py-3">
      <div className="flex gap-3">
        <GalleryThumb item={item} />
        <div className="min-w-0 flex-1">
          {/* Text, not a link: the 44px Edit button below is the card's way in. */}
          <div className="text-sm font-medium text-ink-display">{item.title}</div>
          <div className="text-xs text-ink-muted">{item.caption ?? "No caption"}</div>
          <div className="mt-2">
            <Badge tone="neutral">{item.occasion_label}</Badge>
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <MoveButtons url={item.move_url} itemName={item.title} first={item.first} last={item.last} />
        <GalleryActions item={item} />
      </div>
    </div>
  )
}
